import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from 'components/common-components/ConfirmationModal';
import Error from 'components/Error';
import FormButton from 'components/form-components/FormButton';
import FormLoadingButton from 'components/form-components/FormLoadingButton';
import TextField from 'components/form-components/TextField';
import { validateSqlColumnName } from 'pages/forms/utils';
import { CallAPI } from 'actions/General';
import { CompanyConnectionExportSchema } from '../utils';

const DatabaseExport = ({ companyDetails, getCompanyDetail }) => {
  const { t } = useTranslation();
  const [values, setValues] = useState({
    requestType: 'sql_db',
    externalDbTableName: '',
    existing: false,
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [reImportLoading, setReImportLoading] = useState(false);
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
  const [apiError, setApiError] = useState('');
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    setValues({
      externalDbTableName: companyDetails?.externalExportSetting?.externalDbTableName ?? '',
      requestType: companyDetails?.externalExportSetting?.requestType ?? 'sql_db',
      existing: !!companyDetails?.externalExportSetting,
    });
  }, [companyDetails]);

  const handleChange = (field, value) => {
    if (validateSqlColumnName(value)) {
      setErrors({ ...errors, [field]: undefined });
      setValues({ ...values, [field]: value });
      setTouched({ ...touched, [field]: true });
    }
  };

  const submit = async () => {
    setSaveLoading(true);
    setTouched({});
    setApiError('');
    try {
      await CompanyConnectionExportSchema.validate(values, { abortEarly: false });
      const payload = {
        externalExportSetting: {
          requestType: values.requestType,
          externalDbTableName: values.externalDbTableName,
        },
      };

      let tableExist = false;
      if (!values.existing) {
        const exist = await checkIfTableNameExist(values.externalDbTableName);

        if (exist) {
          setApiError(`Table: ${values.externalDbTableName} already exists`);
          tableExist = true;
        }
      }

      if (!tableExist) {
        const result = await CallAPI(
          'UPDATE_COMPANY_SQL_SETTINGS',
          payload,
          null,
          setSaveLoading,
          setApiError,
        );
        if (result.status) {
          toast.success('Company export setting updated successfully');
          getCompanyDetail();
        }
      }
    } catch (error) {
      const schemaErrors = {};
      error.inner?.forEach((err) => {
        schemaErrors[err.path] = err.message.split('.').at(-1);
      });

      setErrors({ ...schemaErrors });
      setSaveLoading(false);
    }
  };

  const reImportSQLSubmissionsData = async () => {
    setReImportLoading(true);
    setTouched({});
    setApiError('');
    try {
      const result = await CallAPI(
        'REIMPORT_SQL_SUBMISSIONS_FOR_COMPANY',
        {},
        null,
        setReImportLoading,
        setApiError,
        null,
      );

      if (result.status) {
        toast.success('Company export re-import started');
      }
    } catch (error) {
      const schemaErrors = {};
      error.inner?.forEach((err) => {
        schemaErrors[err.path] = err.message.split('.').at(-1);
      });

      setErrors({ ...schemaErrors });
      setReImportLoading(false);
    }
  };

  const checkIfTableNameExist = async (tableName) => {
    const result = await CallAPI(
      'CHECK_EXTERNAL_DB_TABLE_NAME',
      { tableName },
      null,
      setSaveLoading,
      null,
      null,
    );

    if (result.status && result.data.tableExist) return true;
    else return false;
  };

  const deleteExternalSetting = async () => {
    const result = await CallAPI(
      'UPDATE_COMPANY_SQL_SETTINGS',
      { externalExportSetting: null },
      null,
      setDeleteLoading,
      setApiError,
    );
    if (result.status) {
      toast.success('Company export setting deleted successfully');
      getCompanyDetail();
    }
  };

  return (
    <>
      <div className='px-4 py-3'>
        {apiError && (
          <div className='mb-2'>
            <Error msg={apiError} />
          </div>
        )}
        <label className='theme-size-1_2 theme-text-black-1 mb-2'>{t('text_sql_export')}</label>
        <TextField
          name='externalDbTableName'
          value={values['externalDbTableName']}
          displayName={'field_external_db_table_name'}
          placeholder={'field_external_db_table_name'}
          error={errors['externalDbTableName']}
          touched={touched['externalDbTableName']}
          handleChange={handleChange}
          classes='mb-3'
          isHorizontal={true}
          shrinkFieldOnly={true}
          horizontalLabelClass='min-width-176'
          disabled={values.existing}
        />

        <div className='mt-3  w-100 d-flex justify-content-end gap-3'>
          <FormButton text='button_cancel' variant='white-1' />
          {values.existing && (
            <FormLoadingButton
              text='button_delete'
              variant='red-1'
              loading={deleteLoading}
              onClick={() => setDeleteConfirmationModal(true)}
            />
          )}
          {values.existing && (
            <FormLoadingButton
              text='button_reimport'
              variant='blue-1'
              loading={reImportLoading}
              onClick={reImportSQLSubmissionsData}
            />
          )}
          <FormLoadingButton
            onClick={submit}
            loading={saveLoading}
            text='button_save'
            variant='green-1'
          />
        </div>
      </div>
      <ConfirmationModal
        show={deleteConfirmationModal}
        title={t('alert_common_title')}
        message={t('alert_export_setting_deletion_message')}
        actionList={[
          {
            color: 'black-1',
            text: t('button_cancel'),
            onClick: () => setDeleteConfirmationModal(false),
          },
          {
            color: 'red-1',
            text: t('button_delete'),
            onClick: () => {
              deleteExternalSetting();
              setDeleteConfirmationModal(false);
            },
          },
        ]}
      />
    </>
  );
};

DatabaseExport.propTypes = {
  companyDetails: PropTypes.object,
  getCompanyDetail: PropTypes.func.isRequired,
};

export default DatabaseExport;
